import { useAppDispatch, useAppSelector } from "app/hooks";
import { FrontBanner, Header, Loader } from "components";
import { useCommunity } from "components/CommunityContext";
import { HowYouKnowAboutUs, HowYouKnowAboutUsType, MemberProfile, Status } from "models/member";
import { useEffect, useState } from "react";
import { profileActions } from "redux/reducers/profile";
import { selectProfile } from "redux/selectors/profile";
import StepHeader from "./stepper/components/StepHeader";
import DynamicStep from "./stepper/DynamicStep";
import moment from "moment";
import history from 'utils/route-history';
import { authActions } from "redux/reducers/auth";
import { Reccuring } from "helpers/subscriptions";
import { appActions } from "redux/reducers/app";
import profileApi from "api/profile";
import { PROMO_CODE } from "utils/storageKeys";

const DynamicOnboarding = (): JSX.Element => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectProfile);
  const { communityConfig, loading, error } = useCommunity();
  const currentStepConfig = communityConfig?.onboarding[currentStep];
  const totalSteps = communityConfig?.onboarding.length;
  const [isOnboardingFinish, setIsOnboardingFinish] = useState(false);

  const referralCode = user?.onboardingSettings?.referredBy?.referralCode || '';

  useEffect(() => {
    dispatch(appActions.fetchCountries());
    if(!user){
      dispatch(profileActions.getProfile());
    }else{
      if(user.status !== Status.NEW){
        history.push('/');
      }
    }
  }, [user])

  useEffect(() => {
    const initialFormValues: Record<string, any> = {
      email: user?.email,
      membershipPreference: {
        subscriptionId: '',
        priceId: '',
        recurring: '',
        isPriceLocked: false,
        isSubscriptionLocked: false,
      }
    };
    
    if (user?.profile) {
      communityConfig?.onboarding?.forEach((stepConfig: any) => {
        stepConfig.steps?.forEach((field: any) => {

          if (field.component === 'radio-items' && Array.isArray(field.options)) {
            field.options.forEach((option: any) => {
              if (option.textField && user.profile[option.textField as keyof MemberProfile] !== undefined) {
                initialFormValues[option.textField] = user.profile[option.textField as keyof MemberProfile];
              }
            });
          }

          if (field.name === 'promoCode') {
            initialFormValues[field.name] = user.onboardingSettings.promoCode || sessionStorage.getItem(PROMO_CODE) || '';
          }
          if (user.profile[field.name as keyof MemberProfile] !== undefined) {
            if (field.name === 'secondaryCities' && Array.isArray(user.profile.secondaryCities)) {
              initialFormValues[field.name] = user.profile.secondaryCities.map(city => ({
                key: city,
                value: city,
                label: city
              }));
            }
            else if (field.name === 'birthday' && user.profile.birthday) {
              const parsedDate = moment(user.profile.birthday, moment.ISO_8601, true);
              initialFormValues[field.name] = parsedDate.isValid() ? parsedDate.format(field.format || 'DD-MM-YYYY') : user.profile.birthday;
            }
            else if (field.name === 'membershipPreference') {
              initialFormValues[field.name] = {
                subscriptionId: user.profile.membershipPreference?.subscriptionId || '',
                priceId: user.profile.membershipPreference?.priceId || '',
                recurring: user.profile.membershipPreference?.recurring || Reccuring.YEAR,
                isSubscriptionLocked: user.profile.membershipPreference?.isSubscriptionLocked || false,
                isPriceLocked: user.profile.membershipPreference?.isPriceLocked || false,
              };
              initialFormValues['startDate'] = moment().format('DD-MM-YYYY')
            }
            else if (field.name === 'countryOfResidence') {
              const countryValue = user.profile.countryOfResidence;
              const countryOption = field.options?.find((opt: any) => opt.value === countryValue);
              
              initialFormValues[field.name] = {
                key: countryOption?.key || countryValue,
                value: countryValue,
                label: countryValue
              };
            }
            else if (field.name === 'interestedInSpaces') {
              initialFormValues[field.name] = field.component === 'select-multiple' && user.profile.interestedInSpaces
                ? (Array.isArray(user.profile.interestedInSpaces) ? user.profile.interestedInSpaces : [user.profile.interestedInSpaces])
                : (Array.isArray(user.profile.interestedInSpaces) ? user.profile.interestedInSpaces[0] : user.profile.interestedInSpaces);
            }
            else if (field.name === 'whyInterested') {
              if (field.component === 'select-multiple') {
                if (Array.isArray(user.profile.whyInterested)) {
                  initialFormValues[field.name] = user.profile.whyInterested;
                } else if (typeof user.profile.whyInterested === 'string') {

                  const options = field.options?.map((opt: any) => opt.value) || [];
                  const str = user.profile.whyInterested;
                  initialFormValues[field.name] = options.filter((option: string) =>{
                    return str.includes(option)
                  });
                } else {
                  initialFormValues[field.name] = [];
                }
              } else {
                initialFormValues[field.name] = Array.isArray(user.profile.whyInterested)
                  ? user.profile.whyInterested[0]
                  : user.profile.whyInterested;
              }
            }
            else {
              initialFormValues[field.name] = user.profile[field.name as keyof MemberProfile];
            }
          }
        });
      });
    }

    setFormValues((prev) => ({
      ...prev,
      ...initialFormValues
    }));
  }, [user, communityConfig]);

  const updateProfile = (model: Partial<MemberProfile>) => {
    const formats = ['DD-MM-YYYY', 'YYYY-MM-DD', 'MM-DD-YYYY', 'YYYY/MM/DD', 'DD/MM/YYYY', 'MM/DD/YYYY'];
    let parsedDate = moment(model.birthday, formats);

    if (!parsedDate.isValid()) {
      parsedDate = moment(model.birthday);
    }
    const payload = {
      ...model,
      birthday: parsedDate.format("YYYY-MM-DD")
    }
    dispatch(profileActions.updateProfile(payload));
  };

  const finishOnboarding = (data: HowYouKnowAboutUsType) => {
    data.userId = user?._id;
    data.howYouKnowAboutUs = formValues.howYouKnowAboutUs;
    data.otherText = formValues.howYouKnowAboutUsOtherText;
    dispatch(profileActions.confrimMember(data));
    setIsOnboardingFinish(true);
  };

  const handleStepSubmit = async(values: Record<string, any>) => {
    setIsOnboardingFinish(true);
    setFormValues((prev) => ({ ...prev, ...values }));
    if(values.paymentCard){
      await profileApi.setPaymentMethod({ paymentMethodId: values.paymentCard});
      setIsOnboardingFinish(false);
    } else {
      await updateProfile({
        ...formValues,
        ...values,
        countryOfResidence: values.countryOfResidence?.value || formValues.countryOfResidence?.value,
        secondaryCities: formValues.secondaryCities?.map((x: any) => x.value),
        ...(values.interestedInSpaces && {
          interestedInSpaces: Array.isArray(values.interestedInSpaces) ? values.interestedInSpaces : [values.interestedInSpaces]
        }),
        whyInterested: Array.isArray(values.whyInterested) ? values.whyInterested.join(',') : values.whyInterested,
        membershipPreference: {
          ...values.membershipPreference,
          startDate: values?.startDate ? moment.utc(values.startDate, "DD-MM-YYYY").toISOString() : null
        }
      } as Partial<MemberProfile>);
      setIsOnboardingFinish(false);
    }

    if (referralCode !== '' && currentStepConfig.steps?.type == HowYouKnowAboutUs.REFERRAL) {
      // if user has referral code, then skip "ConfirmUser" step
      // and send data with preselected referral option
      const data: HowYouKnowAboutUsType = {
        howYouKnowAboutUs: HowYouKnowAboutUs.REFERRAL,
        referralCode,
      };
      finishOnboarding(data);
    }

    if (currentStep >= totalSteps - 1) {
      finishOnboarding({
        ...formValues,
        ...values,
      })
    }
  };

  const handlePrev = () => {
    if(currentStep === 0){
      dispatch(authActions.logout());
    }else{
      setCurrentStep((prev) => Math.max(0, prev - 1));
    }
  };
  const handleNextStep = () => {
    if(currentStep >= totalSteps - 1){
      return;
    }

    setCurrentStep((prev) => prev + 1)
  };

  if (loading) {
    return (
      <>
        <div className="page">
            <Loader />
        </div>
      </>)
  }

  if (error) {
    return (
      <>
        <div className="page">
          <Header withBackArrow={false} />
          <div className=" container">
            <FrontBanner
              mainText="Something went wrong"
              description={error}
            />
          </div>
        </div>
      </>)
  }

  return (
    <div>
          <StepHeader current={currentStep} total={totalSteps} prev={handlePrev} />
          <div className="step-page">
            {communityConfig?.onboarding?.map((stepConfig: any, index: number) => (
              currentStep === index && (
                <DynamicStep
                  key={`${index}_${stepConfig.title}`}
                  title={currentStepConfig?.title}
                  fields={currentStepConfig?.steps}
                  initialValues={formValues}
                  validationSchema={currentStepConfig?.validationSchema}
                  onSubmit={handleStepSubmit}
                  next={handleNextStep}
                  isOnboardingFinish={isOnboardingFinish}
                />
              )
            ))}
          </div>
    </div>
  );
};

export default DynamicOnboarding;